import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const CUSTOM_VALUE = '__custom__';

/**
 * ResponsibleSelect - owner picker mirroring the Atelier 5 measures table:
 * the union participant list plus an "Other (free text)" fallback (a fusion
 * spans several analyses, so the wanted name may not be in any list yet).
 * A stored value that is no longer in any list is kept visible and editable
 * instead of being silently hidden.
 */
const ResponsibleSelect = ({ value = '', onChange, options = [], selectClassName = 'w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400', inputClassName = 'w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400' }) => {
  const { t } = useTranslation();
  const [custom, setCustom] = useState(false);
  const list = options || [];
  const inList = list.some((o) => o.name === value);
  const showCustom = custom || (!!value && !inList);

  return (
    <div className="space-y-1">
      <select
        value={inList ? value : (showCustom ? CUSTOM_VALUE : '')}
        onChange={(e) => {
          if (e.target.value === CUSTOM_VALUE) {
            setCustom(true);
          } else {
            setCustom(false);
            onChange(e.target.value);
          }
        }}
        className={selectClassName}
      >
        <option value="">—</option>
        {list.map((o) => (
          <option key={o.name} value={o.name}>
            {o.name}{o.role ? ` (${o.role})` : ''}
          </option>
        ))}
        {list.length > 0 && <option disabled>──────────</option>}
        <option value={CUSTOM_VALUE}>{t('fusionPtr.responsibleCustom', 'Autre (saisie libre)...')}</option>
      </select>
      {showCustom && (
        <input
          type="text"
          value={inList ? '' : (value || '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('fusionPtr.responsibleLabel', 'Responsable')}
          className={inputClassName}
        />
      )}
      {!list.length && !showCustom && (
        <p className="text-[10px] text-slate-400 italic leading-snug">
          {t('fusionPtr.noResponsibleOptions', 'Aucun responsable dans les analyses sources — ajoutez-les dans le Cadre (Atelier 1), ils seront synchronisés automatiquement.')}
        </p>
      )}
    </div>
  );
};

ResponsibleSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    role: PropTypes.string,
    analysisName: PropTypes.string,
  })),
  selectClassName: PropTypes.string,
  inputClassName: PropTypes.string,
};

export default ResponsibleSelect;
